import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserPlus, Trash2, Users, BadgeCheck } from 'lucide-react';
import TravellerSearchSelect from '@/components/traveller/TravellerSearchSelect';

const RELATIONS = ['Spouse', 'Child', 'Parent', 'Sibling', 'Friend', 'Colleague', 'Other'];

// BUG 5 — visa status is per traveller PER trip (the destination differs each
// trip), so it lives on the trip's pax entries, not on the global profile.
export const VISA_STATUSES = [
  { value: 'not_required', label: 'Not Required' },
  { value: 'applied', label: 'Applied' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
];

export const visaStatusLabel = (v) => (VISA_STATUSES.find((s) => s.value === v)?.label || '');

export const VisaStatusSelect = ({ value, onChange, testid }) => (
  <Select value={value || ''} onValueChange={onChange}>
    <SelectTrigger className="h-10" data-testid={testid}>
      <SelectValue placeholder="Visa status" />
    </SelectTrigger>
    <SelectContent>
      {VISA_STATUSES.map((s) => (
        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const emptyPax = () => ({ name: '', age: '', relation: '', dob: '', passportNumber: '', visaStatus: '' });

const ageFromDob = (dob) => {
  if (!dob) return '';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const age = Math.floor(diff / (365.25 * 24 * 3600 * 1000));
  return age >= 0 && age < 150 ? age : '';
};

const paxFromTraveller = (c) => ({
  name: c.fullName || c.name || '',
  age: ageFromDob(c.dateOfBirth),
  relation: '',
  dob: (c.dateOfBirth || '').slice(0, 10),
  passportNumber: c.passportNumber || '',
  ucc: c.ucc || '',
  displayUCC: c.displayUCC || '',
  visaStatus: '',
});

// Hoisted row component so the map body in AdditionalPaxEditor stays a simple
// expression — avoids the visual-edits Babel plugin stack-overflow that bites
// member-expression maps inside JSX.
const PaxRow = ({ pax, onPatch, onRemove, index, showVisa }) => (
  <div
    className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end p-3 rounded-xl border border-slate-100 bg-white"
    data-testid={`pax-row-${index}`}
  >
    <div className={showVisa ? 'md:col-span-3' : 'md:col-span-4'}>
      <Input
        placeholder="Full name"
        value={pax.name || ''}
        onChange={(e) => onPatch({ name: e.target.value })}
        data-testid={`pax-name-${index}`}
        className="h-10"
      />
      {pax.displayUCC && (
        <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-mono text-teal-700" data-testid={`pax-linked-${index}`}>
          <BadgeCheck className="h-3 w-3" /> {pax.displayUCC}
        </span>
      )}
    </div>
    <div className={showVisa ? 'md:col-span-1' : 'md:col-span-2'}>
      <Input
        type="number"
        min="0"
        placeholder="Age"
        value={pax.age ?? ''}
        onChange={(e) => onPatch({ age: e.target.value })}
        data-testid={`pax-age-${index}`}
        className="h-10"
      />
    </div>
    <div className={showVisa ? 'md:col-span-2' : 'md:col-span-3'}>
      <Select value={pax.relation || ''} onValueChange={(v) => onPatch({ relation: v })}>
        <SelectTrigger className="h-10" data-testid={`pax-relation-${index}`}>
          <SelectValue placeholder="Relation" />
        </SelectTrigger>
        <SelectContent>
          {RELATIONS.map((r) => (
            <SelectItem key={r} value={r}>{r}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    <div className={showVisa ? 'md:col-span-3' : 'md:col-span-2'}>
      <Input
        placeholder="Passport #"
        value={pax.passportNumber || ''}
        onChange={(e) => onPatch({ passportNumber: (e.target.value || '').toUpperCase() })}
        data-testid={`pax-passport-${index}`}
        className="h-10"
      />
    </div>
    {showVisa && (
      <div className="md:col-span-2">
        <VisaStatusSelect
          value={pax.visaStatus || ''}
          onChange={(v) => onPatch({ visaStatus: v })}
          testid={`pax-visa-${index}`}
        />
      </div>
    )}
    <div className="md:col-span-1 flex justify-end">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        data-testid={`pax-remove-${index}`}
        className="text-rose-500 hover:bg-rose-50 h-10 w-10"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  </div>
);

const AdditionalPaxEditor = ({ value, onChange, showVisa = false }) => {
  const rows = Array.isArray(value) ? value : [];

  const addRow = () => onChange([...rows, emptyPax()]);
  const patchRow = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  // Search-first: pick an existing traveller (pre-fills + links the row), or
  // "Create new" with the typed name when the search comes up empty.
  const handleSelect = (traveller, typedName) => {
    if (traveller) {
      onChange([...rows, paxFromTraveller(traveller)]);
    } else {
      onChange([...rows, { ...emptyPax(), name: typedName || '' }]);
    }
  };

  return (
    <div data-testid="additional-pax-editor">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-teal-700" />
          <div>
            <h3 className="text-base font-semibold text-slate-900">Family / Co-Travellers</h3>
            <p className="text-[11px] text-slate-500">Add others travelling on this trip — no separate contact or UCC needed.</p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={addRow} data-testid="add-pax-btn" className="h-9">
          <UserPlus className="h-4 w-4 mr-2" />Add blank
        </Button>
      </div>

      <div className="mb-4">
        <TravellerSearchSelect onSelect={handleSelect} placeholder="Search existing travellers to add (name, phone or ID)…" />
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-xl" data-testid="pax-empty-state">
          No additional passengers yet. Click "Add Passenger" to include family members.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((pax, i) => (
            <PaxRow
              key={i}
              index={i}
              pax={pax}
              showVisa={showVisa}
              onPatch={(patch) => patchRow(i, patch)}
              onRemove={() => removeRow(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdditionalPaxEditor;
